import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const slides = [
  {
    category: 'Security & Intelligence Solutions',
    brand: 'AVISS',
    title: 'Security Solutions',
    subtitle: 'Aryavihhar Services delivers reliable security solutions including armed & unarmed guarding, access control, and 24/7 premises protection to empower your business growth.',
    image: `${process.env.PUBLIC_URL}/slideshow_images/man_power.png`,
    badge: '🔒 Trained & Disciplined Personnel',
    bg: 'linear-gradient(135deg, #EAF7FB 0%, #F4FBFE 100%)',
  },
  {
    category: 'Integrated Facility & Housing Solutions',
    brand: 'AVISS',
    title: 'Housing Support',
    subtitle: 'Comprehensive facility management for modern residential societies, apartments, and corporate complexes with smart visitor management and professional staff.',
    image: `${process.env.PUBLIC_URL}/slideshow_images/building.png`,
    badge: '🏢 Modern Society Safety',
    bg: 'linear-gradient(135deg, #F4FBFE 0%, #EAF7FB 100%)',
  },
  {
    category: 'Commercial & Industrial Protection',
    brand: 'AVISS',
    title: 'Enterprise Safety',
    subtitle: 'High-vigilance industrial security and corporate premises safety with strict entry protocols, verified manpower, and proactive perimeter monitoring.',
    image: `${process.env.PUBLIC_URL}/slideshow_images/security.png`,
    badge: '🛡️ Background Verified Guards',
    bg: 'linear-gradient(135deg, #EAF7FB 0%, #F4FBFE 100%)',
  },
  {
    category: 'IT & Digitization Solutions',
    brand: 'AVISS',
    title: 'Digital Services',
    subtitle: 'High-speed document scanning, digital archiving, data entry projects, and dependable office IT support to streamline operations and enhance productivity.',
    image: `${process.env.PUBLIC_URL}/slideshow_images/cloud.png`,
    badge: '📡 Smart Tech & Archiving',
    bg: 'linear-gradient(135deg, #F4FBFE 0%, #EAF7FB 100%)',
  },
];

const Slideshow = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const currentSlide = slides[currentIndex];

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, []);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // Auto-slide every 6 seconds
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      goToNext();
    }, 6000);
    return () => clearInterval(timer);
  }, [isPaused, goToNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goToNext, goToPrev]);

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    const el = document.getElementById('housing-services');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* ===== HERO SLIDESHOW (AVA India Split Layout: Text on Left, Image on Right) ===== */}
      <section
        id="slideshow"
        className="relative w-full min-h-[640px] lg:min-h-[720px] pt-28 pb-16 flex items-center overflow-hidden transition-colors duration-700"
        style={{ background: currentSlide.bg }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Subtle background ambient circles */}
        <div 
          className="absolute -top-20 -left-20 w-[450px] h-[450px] rounded-full pointer-events-none opacity-40 blur-[80px]"
          style={{ background: 'radial-gradient(circle, #6FCEEC 0%, transparent 70%)' }}
        />
        <div 
          className="absolute -bottom-20 right-10 w-[450px] h-[450px] rounded-full pointer-events-none opacity-30 blur-[90px]"
          style={{ background: 'radial-gradient(circle, #00B8D9 0%, transparent 70%)' }}
        />

        <div className="max-w-7xl mx-auto px-6 w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* ===== LEFT COLUMN: Editorial Text (Clean, spacious, high readability) ===== */}
            <div className="lg:col-span-6 xl:col-span-7 text-left">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`text-${currentIndex}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* Category chip/tagline (e.g. Human Resource Solutions in AVA India) */}
                  <div className="mb-3">
                    <span className="text-[#1976D2] font-semibold text-sm tracking-wide">
                      {currentSlide.category}
                    </span>
                  </div>

                  {/* Main Display Title (e.g. AVISS Security Solutions / AVISS Housing Support) */}
                  <h1 
                    className="text-[#001726] font-bold tracking-tight leading-[1.12] mb-6 flex flex-wrap items-baseline gap-x-3.5"
                    style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
                  >
                    <span className="text-[#1976D2] font-extrabold tracking-tight">
                      {currentSlide.brand || 'AVISS'}
                    </span>
                    <span className="italic font-serif font-semibold text-[#001726]">
                      {currentSlide.title}
                    </span>
                  </h1>

                  {/* Description Paragraph (Clear, editorial 30px line height) */}
                  <p className="text-[#475569] text-base md:text-[1.1rem] leading-[1.8] max-w-xl mb-9">
                    {currentSlide.subtitle}
                  </p>

                  {/* Two Buttons (Learn More + Contact Us, exactly like AVA India screenshot) */}
                  <div className="flex flex-wrap items-center gap-4">
                    {/* Button 1: Learn More (White pill with blue border) */}
                    <button
                      type="button"
                      onClick={scrollToServices}
                      className="px-8 py-3.5 rounded-full bg-white text-[#1976D2] border-2 border-[#1976D2] hover:bg-[#1976D2] hover:text-white font-semibold text-[0.98rem] transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md"
                    >
                      Learn More
                    </button>

                    {/* Button 2: Contact Us (Filled bright blue pill CTA) */}
                    <button
                      type="button"
                      onClick={scrollToContact}
                      className="px-8 py-3.5 rounded-full text-white font-semibold text-[0.98rem] border-0 cursor-pointer shadow-md transition-all duration-200 hover:brightness-105 hover:shadow-lg"
                      style={{
                        background: 'linear-gradient(135deg, #1976D2 0%, #00B8D9 100%)',
                        boxShadow: '0 6px 22px rgba(25, 118, 210, 0.3)',
                      }}
                    >
                      Contact Us
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* ===== RIGHT COLUMN: Separate Graphic / Image Presentation (Frameless, clean float) ===== */}
            <div className="lg:col-span-6 xl:col-span-5 flex justify-center items-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`img-${currentIndex}`}
                  initial={{ opacity: 0, scale: 0.94, y: 15 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: -15 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full max-w-[560px] relative flex flex-col items-center justify-center"
                >
                  {/* Frameless floating image presentation - transparent images blend naturally */}
                  <div className="relative w-full flex items-center justify-center">
                    <img
                      src={currentSlide.image}
                      alt={currentSlide.title}
                      className="w-full h-auto max-h-[380px] md:max-h-[420px] object-contain transition-transform duration-700 hover:scale-105 filter drop-shadow-[0_15px_35px_rgba(25,118,210,0.18)]"
                    />
                  </div>

                  {/* Clean Floating Info Badge below graphic */}
                  <div className="mt-4 inline-flex items-center gap-3 bg-white/90 backdrop-blur-md px-5 py-2 rounded-full border border-[#2C61B924] shadow-sm">
                    <span className="text-[#001726] font-medium text-xs md:text-sm">
                      {currentSlide.badge}
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1976D2]" />
                    <span className="text-[#1976D2] font-bold text-xs md:text-sm">
                      0{currentIndex + 1} / 0{slides.length}
                    </span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

          </div>

          {/* ===== CENTERED SLIDE PAGINATION DOTS (AVA India style: ● ○ ○ ○) ===== */}
          <div className="flex justify-center items-center gap-2.5 mt-14">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goToSlide(i)}
                className={`transition-all duration-300 border-0 p-0 cursor-pointer rounded-full ${
                  currentIndex === i
                    ? 'w-7 h-2.5 bg-[#1976D2]'
                    : 'w-2.5 h-2.5 bg-[#001726]/20 hover:bg-[#001726]/40'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== FLOATING WHATSAPP BUTTON (AVA India Screenshot Feature) ===== */}
      <a
        href="https://wa.me/919234804849"
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-7 right-7 z-[1990] w-14 h-14 rounded-full bg-[#22C55E] text-white flex items-center justify-center shadow-2xl hover:scale-110 active:scale-95 transition-all duration-200 group cursor-pointer"
        style={{
          boxShadow: '0 8px 30px rgba(34, 197, 94, 0.45)',
        }}
        aria-label="Chat with Us on WhatsApp"
        title="Chat with Us on WhatsApp"
      >
        <span className="absolute inset-0 rounded-full bg-[#22C55E] opacity-75 animate-ping -z-10" />
        <svg viewBox="0 0 32 32" className="w-7 h-7" fill="currentColor">
          <path d="M16.01 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.25.58 4.44 1.69 6.37L3.2 28.8l6.59-1.67a12.76 12.76 0 0 0 6.22 1.61h.01c7.06 0 12.79-5.73 12.79-12.8 0-3.42-1.33-6.64-3.76-9.06A12.7 12.7 0 0 0 16.01 3.2zm0 23.38h-.01a10.6 10.6 0 0 1-5.4-1.47l-.39-.23-3.91.99 1.04-3.82-.25-.4a10.62 10.62 0 0 1-1.62-5.7c0-5.88 4.77-10.66 10.65-10.66 2.84 0 5.52 1.11 7.53 3.12a10.57 10.57 0 0 1 3.11 7.53c0 5.88-4.78 10.64-10.65 10.64zm5.84-8.06c-.32-.16-1.89-.94-2.18-1.05-.29-.11-.5-.16-.71.16-.21.32-.82 1.05-1.01 1.27-.19.21-.38.24-.7.08-.32-.16-1.35-.49-2.57-1.57-.95-.84-1.59-1.88-1.78-2.2-.19-.32-.02-.49.14-.65.14-.14.32-.38.48-.56.16-.19.21-.32.32-.54.11-.21.05-.4-.03-.56-.08-.16-.71-1.72-.98-2.36-.25-.61-.5-.52-.7-.53h-.6c-.21 0-.56.08-.85.4s-1.12 1.09-1.12 2.66 1.15 3.08 1.31 3.3c.16.21 2.26 3.45 5.48 4.83.77.33 1.37.52 1.84.66.77.24 1.46.2 2.01.12.61-.09 1.89-.77 2.16-1.51.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37z" />
        </svg>
      </a>
    </>
  );
};

export default Slideshow;